import { Component } from '@theme/component';

/**
 * SD Carousel Component
 *
 * A multi-item carousel with peek functionality, built on the same solid
 * patterns as sd-slider:
 * - IntersectionObserver for efficient visibility tracking
 * - AbortController for clean event cleanup
 * - CSS scroll-snap for native smooth scrolling
 * - Touch/pointer drag support
 *
 * Unlike sd-slider (full-width slides), this handles:
 * - Multiple visible items with configurable columns
 * - Peek functionality (partial next item visible)
 * - Page margin gutters
 * - "At end" detection to prevent overscroll
 *
 * @typedef {Object} SDCarouselRefs
 * @property {HTMLElement} scroller - Scrollable container
 * @property {HTMLElement[]} slide - Array of slide elements
 * @property {HTMLButtonElement[]} [dot] - Array of dot navigation buttons
 * @property {HTMLButtonElement} [prev] - Previous button
 * @property {HTMLButtonElement} [next] - Next button
 * @property {HTMLElement} [liveRegion] - ARIA live region for announcements
 *
 * @extends {Component<SDCarouselRefs>}
 */
class SDCarousel extends Component {
  requiredRefs = ['scroller'];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {IntersectionObserver|null} */
  #intersectionObserver = null;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #isAtStart = true;

  /** @type {boolean} */
  #isAtEnd = false;

  /** Set by `data-loop`: next at the end goes back to the first item, previous at the start to the last. */
  #loop = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#loop = this.hasAttribute('data-loop');

    const slides = this.refs.slide;
    if (!this.refs.scroller || !slides?.length) return;

    this.#setupIntersectionObserver();
    this.#setupScrollListener(signal);
    this.#setupEventListeners(signal);

    // Only setup drag handling if motion is allowed
    if (!this.#prefersReducedMotion()) {
      this.#setupDragHandling(signal);
    }

    // Initialize state
    this.#updateScrollState();
    this.#setCurrentIndex(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
  }

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get current slide index (first visible item)
   * @returns {number}
   */
  get currentIndex() {
    return this.#currentIndex;
  }

  /**
   * Check if carousel is at the start
   * @returns {boolean}
   */
  get isAtStart() {
    return this.#isAtStart;
  }

  /**
   * Check if carousel is at the end
   * @returns {boolean}
   */
  get isAtEnd() {
    return this.#isAtEnd;
  }

  // ==========================================================================
  // Public Methods
  // ==========================================================================

  /**
   * Navigate to a specific slide by index
   * @param {number} index - Target slide index
   */
  select(index) {
    const slides = this.refs.slide;
    const scroller = this.refs.scroller;
    if (!slides?.length || !scroller) return;

    // Clamp index to valid range
    const targetIndex = Math.max(0, Math.min(index, slides.length - 1));
    const targetSlide = slides[targetIndex];
    if (!targetSlide) return;

    // Calculate scroll position
    // Account for the container's padding (gutter) on the first item
    const scrollerRect = scroller.getBoundingClientRect();
    const slideRect = targetSlide.getBoundingClientRect();
    const currentScroll = scroller.scrollLeft;

    // Calculate where the slide is relative to the scroller's visible area
    const slideOffset = slideRect.left - scrollerRect.left + currentScroll;

    // Get the scroll padding (gutter) from CSS
    const computedStyle = getComputedStyle(scroller);
    const scrollPadding = parseFloat(computedStyle.scrollPaddingInlineStart) || 0;

    // Target scroll position: slide offset minus scroll padding
    const targetScroll = slideOffset - scrollPadding;

    // Clamp to valid scroll range
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const clampedScroll = Math.max(0, Math.min(targetScroll, maxScroll));

    // Scroll to position
    const behavior = this.#prefersReducedMotion() ? 'auto' : 'smooth';
    scroller.scrollTo({
      left: clampedScroll,
      behavior,
    });
  }

  /**
   * Go to next slide, or back to the first from the end when looping
   */
  next() {
    if (this.#isAtEnd) {
      if (this.#loop && !this.#isAtStart) this.select(0);
      return;
    }
    this.select(this.#currentIndex + 1);
  }

  /**
   * Go to previous slide, or on to the last from the start when looping
   */
  previous() {
    if (this.#isAtStart) {
      if (this.#loop && !this.#isAtEnd) this.select((this.refs.slide?.length ?? 1) - 1);
      return;
    }
    this.select(this.#currentIndex - 1);
  }

  // ==========================================================================
  // Private Methods
  // ==========================================================================

  /**
   * Check if user prefers reduced motion
   * @returns {boolean}
   */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Setup IntersectionObserver to track the first visible slide
   */
  #setupIntersectionObserver() {
    const slides = this.refs.slide;
    const scroller = this.refs.scroller;
    if (!slides?.length || !scroller) return;

    // Track which slides are currently visible
    const visibleSlides = new Set();

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slide = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            visibleSlides.add(slide);
          } else {
            visibleSlides.delete(slide);
          }
        }

        // Find the first visible slide (lowest index)
        let firstVisibleIndex = -1;
        slides.forEach((slide, index) => {
          if (visibleSlides.has(slide) && (firstVisibleIndex === -1 || index < firstVisibleIndex)) {
            firstVisibleIndex = index;
          }
        });

        if (firstVisibleIndex !== -1 && firstVisibleIndex !== this.#currentIndex) {
          this.#setCurrentIndex(firstVisibleIndex);
        }
      },
      {
        root: scroller,
        threshold: 0.5,
        rootMargin: '0px',
      }
    );

    slides.forEach((slide) => this.#intersectionObserver?.observe(slide));
  }

  /**
   * Setup scroll listener for at-start/at-end detection
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #setupScrollListener(signal) {
    const scroller = this.refs.scroller;
    if (!scroller) return;

    // Use passive listener for better scroll performance
    scroller.addEventListener(
      'scroll',
      () => {
        // Debounce with requestAnimationFrame
        requestAnimationFrame(() => {
          this.#updateScrollState();
        });
      },
      { signal, passive: true }
    );
  }

  /**
   * Update isAtStart and isAtEnd state based on scroll position
   */
  #updateScrollState() {
    const scroller = this.refs.scroller;
    if (!scroller) return;

    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    const maxScroll = scrollWidth - clientWidth;

    // Small threshold to account for sub-pixel rounding
    const threshold = 2;

    const wasAtStart = this.#isAtStart;
    const wasAtEnd = this.#isAtEnd;

    this.#isAtStart = scrollLeft <= threshold;
    this.#isAtEnd = scrollLeft >= maxScroll - threshold;

    // Update button states if changed
    if (wasAtStart !== this.#isAtStart || wasAtEnd !== this.#isAtEnd) {
      this.#updateButtonStates();
    }

    // Dispatch events for gradient handling
    if (wasAtEnd !== this.#isAtEnd) {
      this.classList.toggle('sd-carousel--at-end', this.#isAtEnd);
    }
    if (wasAtStart !== this.#isAtStart) {
      this.classList.toggle('sd-carousel--at-start', this.#isAtStart);
    }
  }

  /**
   * Setup event listeners
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #setupEventListeners(signal) {
    const { prev, next, dot } = this.refs;

    // Navigation buttons
    prev?.addEventListener('click', () => this.previous(), { signal });
    next?.addEventListener('click', () => this.next(), { signal });

    // Dots
    dot?.forEach((dotEl) => {
      dotEl.addEventListener(
        'click',
        () => {
          const index = parseInt(dotEl.dataset.slideIndex || '0', 10);
          this.select(index);
        },
        { signal }
      );
    });

    // Keyboard navigation (when component has focus)
    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.previous();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        }
      },
      { signal }
    );
  }

  /**
   * Setup drag/swipe handling
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #setupDragHandling(signal) {
    const slides = this.refs.slide;
    const scroller = this.refs.scroller;
    if (!slides?.length || !scroller) return;

    let startX = 0;
    let startScrollLeft = 0;
    /** @type {AbortController|null} */
    let dragController = null;

    /**
     * @param {PointerEvent} e
     */
    const onPointerDown = (e) => {
      if (slides.length <= 1) return;
      // Don't start drag on interactive elements
      if (e.target instanceof Element && e.target.closest('a, button, input, select, textarea')) return;

      this.#isDragging = true;
      this.setAttribute('dragging', '');
      startX = e.pageX;
      startScrollLeft = scroller.scrollLeft;

      // Create controller for drag events
      dragController = new AbortController();
      const dragSignal = dragController.signal;

      document.addEventListener('pointermove', onPointerMove, { signal: dragSignal });
      document.addEventListener('pointerup', onPointerUp, { signal: dragSignal });
      document.addEventListener('pointercancel', onPointerUp, { signal: dragSignal });
    };

    /**
     * @param {PointerEvent} e
     */
    const onPointerMove = (e) => {
      if (!this.#isDragging) return;
      e.preventDefault();

      const x = e.pageX;
      const walk = (startX - x) * 1.2; // Drag sensitivity
      scroller.scrollLeft = startScrollLeft + walk;
    };

    const onPointerUp = () => {
      this.#isDragging = false;
      this.removeAttribute('dragging');
      dragController?.abort();
      dragController = null;
    };

    scroller.addEventListener('pointerdown', onPointerDown, { signal });
  }

  /**
   * Update current index and sync UI
   * @param {number} index - New current index
   */
  #setCurrentIndex(index) {
    const slides = this.refs.slide;
    const dots = this.refs.dot;
    const { liveRegion } = this.refs;

    this.#currentIndex = index;

    // Update dots
    dots?.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    // Update button states
    this.#updateButtonStates();

    // Announce to screen readers
    if (liveRegion && slides?.length) {
      liveRegion.textContent = `Item ${index + 1} of ${slides.length}`;
    }

    // Dispatch custom event for external listeners (like gradient toggle)
    this.dispatchEvent(
      new CustomEvent('carousel:change', {
        detail: { index, total: slides?.length || 0 },
        bubbles: true,
      })
    );
  }

  /**
   * Update prev/next button disabled states. A looping carousel only stops when everything fits,
   * which is when it is at both ends at once.
   */
  #updateButtonStates() {
    const { prev, next } = this.refs;
    const fits = this.#isAtStart && this.#isAtEnd;

    if (prev) {
      prev.disabled = this.#loop ? fits : this.#isAtStart;
    }
    if (next) {
      next.disabled = this.#loop ? fits : this.#isAtEnd;
    }
  }
}

// Register custom element
if (!customElements.get('sd-carousel-component')) {
  customElements.define('sd-carousel-component', SDCarousel);
}
